import { CounterpartiesDropdown } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/CounterpartyDropdown';
import { InvestorsDropdown } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/InvestorsDropdown';
import { PersonHoverInfo } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/PersonHoverInfo';
import { UnassignedRoleBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/UnassignedRoleBadge';
import {
	CounterpartyOptionGroupKey,
	DisplayWorkflowStatus,
	DisplayWorkflowStatusColorMap,
	getDisplayWorkflowStatusText,
	getPersonStatusText,
	mapToDisplayStatus,
	PersonStatusColorMap,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { createDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createStatusColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/StatusColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { CounterpartyCombinedFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/CounterpartyCombinedFilter';
import { DateRangePickerFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/DateRangePickerFilter';
import {
	InvestorFilter,
	NO_SIGNATURE_REQUIRED_FILTER_VALUE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/Filters/InvestorFilter';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import {
	DocumentWorkflowInstanceSignatureStatus,
	InvestmentProgrammeDocumentRes,
} from '@dealstack/shared/lib/generated';
import { Flex, Text, Tooltip } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

type FooterValues = {
	investor: {
		SIGNED: number;
		CREATED: number;
		NO_SIGNATURE_REQUIRED?: number;
	};
	counterparty: {
		SIGNED: number;
		CREATED: number;
	};
	status: Record<DisplayWorkflowStatus, number>;
	total: number;
};

const initialFooterValues: FooterValues = {
	investor: { SIGNED: 0, CREATED: 0, NO_SIGNATURE_REQUIRED: 0 },
	counterparty: { SIGNED: 0, CREATED: 0 },
	status: { COMPLETED: 0, IN_PROGRESS: 0, AWAITING_DATE: 0, REVOKED: 0, ERROR: 0 },
	total: 0,
};

export const createInvestmentProgrammeDocumentsColumns = (
	counterpartyOptions: Array<{
		value: string;
		label: string;
		groupKey: CounterpartyOptionGroupKey;
		groupLabel: string;
	}>,
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>,
	footerValues = initialFooterValues,
	classifierOptions: Array<{ value: string; label: string }> = []
): ExtendedColumnDef<InvestmentProgrammeDocumentRes>[] => {
	const investorAppliedFilterOptions = [
		{
			value: DocumentWorkflowInstanceSignatureStatus.CREATED,
			label: tMipManage('documents_page.signature_status.pending'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.SIGNED,
			label: tMipManage('documents_page.signature_status.signed'),
		},
		{
			value: NO_SIGNATURE_REQUIRED_FILTER_VALUE,
			label: tMipManage('documents_page.signature_status.no_signature_required'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.DECLINED,
			label: tMipManage('documents_page.signature_status.declined'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.EXPIRED,
			label: tMipManage('documents_page.signature_status.expired'),
		},
		{
			value: DocumentWorkflowInstanceSignatureStatus.ERROR,
			label: tMipManage('documents_page.signature_status.error'),
		},
	];
	const counterpartyStatusAppliedFilterOptions = counterpartyOptions
		.filter((option) => option.groupKey === CounterpartyOptionGroupKey.SIGNATURE_STATUS)
		.map((option) => ({
			value: option.value.replace('status:', ''),
			label: option.label,
		}));
	const counterpartyRoleAppliedFilterOptions = counterpartyOptions
		.filter((option) => option.groupKey === CounterpartyOptionGroupKey.DOCUMENT_ROLE)
		.map((option) => ({
			value: option.value.replace('role:', ''),
			label: option.label,
		}));

	return [
		createTextColumn({
			id: 'documentName',
			headerText: tMipManage('documents_page.columns.document_name.header'),
			columnLabel: tMipManage('documents_page.columns.document_name.header'),
			filterText: tMipManage('documents_page.columns.document_name.header'),
			accessorFn: (row) => row.templateName ?? '-',
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filterVariant: 'text',
			text: tMipManage('documents_page.columns.document_name.footer_text'),
			footerValue: footerValues.total,
		}),
		createStatusColumn({
			id: 'investor',
			accessorFn: (row) => row.investor,
			headerText: tMipManage('documents_page.columns.investor.header'),
			columnLabel: tMipManage('documents_page.columns.investor.header'),
			filterText: tMipManage('documents_page.columns.investor.header'),
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filter: ({ column }) => (
				<InvestorFilter
					column={column}
					filterText={tMipManage('documents_page.columns.investor.header')}
				/>
			),
			appliedFilterFields: [
				{ key: 'investorName', type: 'text' },
				{
					key: 'signatureStatuses',
					type: 'multi-select',
					options: investorAppliedFilterOptions,
				},
			],
			cell: ({ row }) => {
				const investors = row.original.investor;

				if (investors.length === 0) {
					return <Text>-</Text>;
				}

				if (investors.length === 1) {
					const investor = investors[0];
					const name =
						`${investor.firstName ?? ''} ${investor.lastName ?? ''}`.trim() || '-';
					const isNoSignatureRequired = investor.status == null;
					const color = isNoSignatureRequired
						? PersonStatusColorMap.NO_SIGNATURE_REQUIRED
						: PersonStatusColorMap[
								investor.status ?? DocumentWorkflowInstanceSignatureStatus.CREATED
							];
					const cellContent = (
						<Text c={color}>
							{investor.firstName} {investor.lastName}
						</Text>
					);
					if (isNoSignatureRequired) {
						return (
							<Tooltip
								label={`${name} • ${getPersonStatusText(null, tMipManage)}`}
								withArrow
							>
								<span>{cellContent}</span>
							</Tooltip>
						);
					}
					return cellContent;
				}

				return <InvestorsDropdown investors={investors} />;
			},
			statusDefinition: [
				{
					status: 'CREATED',
					color: PersonStatusColorMap.CREATED,
					text: tMipManage('documents_page.status_definition.created'),
				},
				{
					status: 'SIGNED',
					color: PersonStatusColorMap.SIGNED,
					text: tMipManage('documents_page.status_definition.signed'),
				},
				{
					status: 'NO_SIGNATURE_REQUIRED',
					color: PersonStatusColorMap.NO_SIGNATURE_REQUIRED,
					text: tMipManage('documents_page.status_definition.no_signature_required'),
				},
			],
			footerValue: footerValues.investor,
		}),
		createStatusColumn({
			id: 'counterparty',
			accessorFn: (row) => row.counterparties,
			headerText: tMipManage('documents_page.columns.counterparty.header'),
			columnLabel: tMipManage('documents_page.columns.counterparty.header'),
			filterText: tMipManage('documents_page.columns.counterparty.header'),
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filter: ({ column }) => (
				<CounterpartyCombinedFilter
					column={column}
					filterText={tMipManage('documents_page.columns.counterparty.header')}
					options={counterpartyOptions}
				/>
			),
			appliedFilterFields: [
				{ key: 'counterpartyName', type: 'text' },
				{
					key: 'signatureStatuses',
					type: 'multi-select',
					options: counterpartyStatusAppliedFilterOptions,
				},
				{
					key: 'roleNames',
					type: 'multi-select',
					options: counterpartyRoleAppliedFilterOptions,
				},
			],
			cell: ({ row }) => {
				const counterparties = row.original.counterparties;

				if (counterparties.length === 0) {
					return;
				}

				if (counterparties.length === 1) {
					const counterparty = counterparties[0];
					if (counterparty.assigned) {
						return <PersonHoverInfo person={counterparty} />;
					}

					return <UnassignedRoleBadge roleName={counterparty.roleName} />;
				}

				return <CounterpartiesDropdown counterparties={counterparties} />;
			},
			statusDefinition: [
				{
					status: 'SIGNED',
					color: PersonStatusColorMap.SIGNED,
					text: tMipManage('documents_page.status_definition.signed'),
				},
				{
					status: 'CREATED',
					color: PersonStatusColorMap.CREATED,
					text: tMipManage('documents_page.status_definition.created'),
				},
			],
			footerValue: footerValues.counterparty,
		}),
		createStatusColumn({
			id: 'status',
			size: 100,
			headerText: tMipManage('documents_page.columns.status.header'),
			columnLabel: tMipManage('documents_page.columns.status.header'),
			filterText: tMipManage('documents_page.columns.status.header'),
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: [
					{
						value: DisplayWorkflowStatus.COMPLETED,
						label: tMipManage('documents_page.workflow_status.completed'),
					},
					{
						label: getDisplayWorkflowStatusText(
							DisplayWorkflowStatus.AWAITING_DATE,
							tMipManage
						),
						value: DisplayWorkflowStatus.AWAITING_DATE,
					},
					{
						value: DisplayWorkflowStatus.IN_PROGRESS,
						label: tMipManage('documents_page.workflow_status.in_progress'),
					},
					{
						value: DisplayWorkflowStatus.REVOKED,
						label: tMipManage('documents_page.workflow_status.revoked'),
					},
					{
						value: DisplayWorkflowStatus.ERROR,
						label: tMipManage('documents_page.workflow_status.error'),
					},
				],
			},
			cell: ({ row }) => {
				const displayStatus = mapToDisplayStatus(row.original.workflowStatus, row.original);

				return (
					<Flex align="center" justify="start" w="100%" miw="0" gap="0.25rem">
						<div
							style={{
								width: '0.375rem',
								height: '0.375rem',
								backgroundColor: DisplayWorkflowStatusColorMap[displayStatus],
								borderRadius: '50%',
								padding: '0.06rem',
							}}
						/>
						<Text>{getDisplayWorkflowStatusText(displayStatus, tMipManage)}</Text>
					</Flex>
				);
			},
			statusDefinition: [
				{
					status: DisplayWorkflowStatus.COMPLETED,
					color: DisplayWorkflowStatusColorMap[DisplayWorkflowStatus.COMPLETED],
					text: tMipManage('documents_page.status_definition.completed'),
				},
				{
					status: DisplayWorkflowStatus.AWAITING_DATE,
					color: DisplayWorkflowStatusColorMap[DisplayWorkflowStatus.AWAITING_DATE],
					text: getDisplayWorkflowStatusText(
						DisplayWorkflowStatus.AWAITING_DATE,
						tMipManage
					),
				},
				{
					status: DisplayWorkflowStatus.IN_PROGRESS,
					color: DisplayWorkflowStatusColorMap[DisplayWorkflowStatus.IN_PROGRESS],
					text: tMipManage('documents_page.status_definition.in_progress'),
				},
				{
					status: DisplayWorkflowStatus.REVOKED,
					color: DisplayWorkflowStatusColorMap[DisplayWorkflowStatus.REVOKED],
					text: tMipManage('documents_page.status_definition.revoked'),
				},
				{
					status: DisplayWorkflowStatus.ERROR,
					color: DisplayWorkflowStatusColorMap[DisplayWorkflowStatus.ERROR],
					text: tMipManage('documents_page.status_definition.error'),
				},
			],
			footerValue: footerValues.status,
		}),
		createTextColumn({
			id: 'dealClassifierIds',
			size: 160,
			headerText: tMipManage('documents_page.columns.classification.header'),
			columnLabel: tMipManage('documents_page.columns.classification.header'),
			filterText: tMipManage('documents_page.columns.classification.header'),
			accessorFn: (row) => row.classifiers?.map((c) => c.name).join(', ') ?? '',
			enableSorting: false,
			enableGlobalFilter: false,
			showFilter: true,
			showFooter: false,
			filterVariant: 'multi-select',
			mantineFilterMultiSelectProps: {
				data: classifierOptions,
			},
			cell: ({ row }) => {
				const names = (row.original.classifiers ?? []).map((c) => c.name);
				if (names.length === 0) {
					return <Text>-</Text>;
				}
				return (
					<Tooltip label={names.join(', ')} withArrow>
						<Text
							style={{
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
							}}
						>
							{names.join(', ')}
						</Text>
					</Tooltip>
				);
			},
		}),
		createTextColumn({
			id: 'investorId',
			headerText: tMipManage('documents_page.columns.investor_id.header'),
			columnLabel: tMipManage('documents_page.columns.investor_id.header'),
			filterText: tMipManage('documents_page.columns.investor_id.header'),
			enableSorting: false,
			enableGlobalFilter: false,
			showFilter: true,
			showFooter: false,
			filterVariant: 'text',
		}),
		createTextColumn({
			id: 'investmentWorkflowId',
			headerText: tMipManage('documents_page.columns.investment.header'),
			columnLabel: tMipManage('documents_page.columns.investment.header'),
			filterText: tMipManage('documents_page.columns.investment.filter'),
			enableSorting: false,
			enableGlobalFilter: false,
			showFilter: true,
			showFooter: false,
			filterVariant: 'text',
		}),
		createDateColumn({
			id: 'createdAt',
			size: 100,
			headerText: tMipManage('documents_page.columns.created_at.header'),
			columnLabel: tMipManage('documents_page.columns.created_at.header'),
			filterText: tMipManage('documents_page.columns.created_at.header'),
			accessorKey: 'createdAt',
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			filter: ({ column }) => (
				<DateRangePickerFilter
					column={column}
					placeholder={tMipManage('documents_page.columns.created_at.header')}
				/>
			),
		}),
		createDateColumn({
			id: 'updatedAt',
			size: 100,
			headerText: tMipManage('documents_page.columns.updated_at.header'),
			columnLabel: tMipManage('documents_page.columns.updated_at.header'),
			filterText: tMipManage('documents_page.columns.updated_at.header'),
			accessorKey: 'updatedAt',
			enableGlobalFilter: true,
			showFilter: true,
			showFooter: true,
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			filter: ({ column }) => (
				<DateRangePickerFilter
					column={column}
					placeholder={tMipManage('documents_page.columns.updated_at.header')}
				/>
			),
		}),
	];
};
